import { useState, useEffect } from "react";
import api, { formatError } from "@/lib/api";
import { Save, Loader2, SlidersHorizontal } from "lucide-react";

/**
 * BrandBarSettings — zentrale Feineinstellung für den schwarzen Marken-Balken.
 * scope="email" → Logo-Höhe + Innenabstand (px) für E-Mails.
 * scope="pdf"   → Logo-Höhe + Balkenhöhe (mm) + Tagline + Fußzeile für Dokumente.
 * Speichert nach POST /api/admin/settings/brand_bar (Merge im Backend).
 */
const EMAIL_FIELDS = [
  { key: "email_logo_height", label: "Logo-Höhe (px)", min: 14, max: 60, step: 1 },
  { key: "email_pad_v", label: "Innenabstand oben/unten (px)", min: 0, max: 40, step: 1 },
  { key: "email_pad_h", label: "Innenabstand links/rechts (px)", min: 0, max: 60, step: 1 },
];
const PDF_FIELDS = [
  { key: "pdf_logo_height", label: "Logo-Höhe (px)", min: 12, max: 64, step: 1 },
  { key: "pdf_bar_height", label: "Balkenhöhe (px)", min: 24, max: 100, step: 1 },
];

export default function BrandBarSettings({ scope = "email" }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const fields = scope === "pdf" ? PDF_FIELDS : EMAIL_FIELDS;

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: "", text: "" }), 3000); };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings/brand_bar");
      setCfg(data.value || {});
    } catch (e) {
      flash("error", formatError(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setField = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Nur die Felder dieses Scopes senden (Backend merged mit dem Rest)
      const payload = {};
      fields.forEach((f) => { payload[f.key] = Number(cfg[f.key]); });
      if (scope === "pdf") {
        payload.pdf_tagline = cfg.pdf_tagline ?? "";
        payload.pdf_footer_text = cfg.pdf_footer_text ?? "";
      }
      const { data } = await api.post("/admin/settings/brand_bar", { value: payload });
      flash("success", "Balken-Einstellungen gespeichert");
      // Backend liefert normalisierte Werte nicht zurück → neu laden
      load();
    } catch (e) {
      flash("error", formatError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="text-xs text-[#999] py-4">Lade Balken-Einstellungen …</div>;

  return (
    <div className="bg-white border border-[#E5E5E5] p-5 space-y-4" data-testid={`brand-bar-settings-${scope}`}>
      <div className="flex items-center gap-2">
        <SlidersHorizontal size={16} className="text-[#0A0A0A]" />
        <h3 className="text-sm font-bold text-[#0A0A0A]">
          Marken-Balken · {scope === "pdf" ? "Dokumente (PDF)" : "E-Mails"}
        </h3>
      </div>
      <p className="text-xs text-[#666]">
        {scope === "pdf"
          ? "Feineinstellung des schwarzen Logo-Balkens im PDF-Kopf (volle Breite zwischen den Seitenrändern)."
          : "Feineinstellung des schwarzen Logo-Balkens im E-Mail-Kopf."}
      </p>

      {msg.text && (
        <div className={`px-3 py-2 border text-sm ${msg.type === "success" ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700"}`}>
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {fields.map((f) => (
          <label key={f.key} className="block">
            <span className="text-[11px] font-medium text-[#666] uppercase tracking-wider">{f.label}</span>
            <input
              type="number"
              min={f.min} max={f.max} step={f.step}
              value={cfg[f.key] ?? ""}
              onChange={(e) => setField(f.key, e.target.value)}
              data-testid={`brand-bar-${f.key}`}
              className="mt-1 w-full h-10 px-3 border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
            />
          </label>
        ))}
      </div>

      {scope === "pdf" && (
        <div className="grid grid-cols-1 gap-4">
          <label className="block">
            <span className="text-[11px] font-medium text-[#666] uppercase tracking-wider">Tagline (rechts im Balken)</span>
            <input
              value={cfg.pdf_tagline ?? ""}
              onChange={(e) => setField("pdf_tagline", e.target.value)}
              data-testid="brand-bar-pdf_tagline"
              className="mt-1 w-full h-10 px-3 border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium text-[#666] uppercase tracking-wider">Fußzeile (Rechtszeile unten)</span>
            <textarea
              value={cfg.pdf_footer_text ?? ""}
              onChange={(e) => setField("pdf_footer_text", e.target.value)}
              data-testid="brand-bar-pdf_footer_text"
              className="mt-1 w-full h-16 px-3 py-2 border border-[#E5E5E5] text-xs bg-white focus:outline-none resize-y"
            />
          </label>
        </div>
      )}

      <button
        onClick={save} disabled={saving}
        data-testid={`brand-bar-save-${scope}`}
        className="h-10 px-6 bg-[#0A0A0A] text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Balken speichern
      </button>
    </div>
  );
}
